import { useState, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { AnimatePresence, motion } from "framer-motion"
import { AuthLayout } from "@/components/layouts/AuthLayout"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { Button } from "@/components/ui/button"

import GoogleIcon from "@assets/images/icons/google.svg"
import BlendIcon from "@assets/images/icons/blend.svg"

export default function Login() {
  const { csrf_token, email_sent_to, flash } = usePage().props
  const [wantsDifferentEmail, setWantsDifferentEmail] = useState(false)

  const showCodeEntry = !!email_sent_to && !wantsDifferentEmail

  const emailForm = useForm({ email: email_sent_to || "" })
  const codeForm = useForm({ email: email_sent_to || "", code: "" })

  useEffect(() => {
    if (email_sent_to) {
      setWantsDifferentEmail(false)
      codeForm.setData("email", email_sent_to)
    }
  }, [email_sent_to])

  function handleEmailSubmit(e) {
    e.preventDefault()
    setWantsDifferentEmail(false)
    emailForm.post("/login", { preserveScroll: true })
  }

  function handleCodeSubmit(e) {
    e.preventDefault()
    codeForm.post("/auth/verify", { preserveScroll: true })
  }

  function handleResend(e) {
    e.preventDefault()
    setWantsDifferentEmail(false)
    emailForm.post("/login", { preserveScroll: true })
  }

  function handleUseDifferentEmail() {
    setWantsDifferentEmail(true)
    codeForm.setData("code", "")
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-8">
        <Card>

          <CardHeader className="flex flex-col items-center">
            <a href="https://decodekit.ai" className="flex items-center gap-2 self-center">
              <BlendIcon className="h-7 w-7 stroke-orange-500" />
              <div className="font-semibold text-2xl">DecodeKit</div>
            </a>
            <AnimatePresence mode="wait">
              <motion.div
                key={showCodeEntry ? "code-desc" : "email-desc"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <CardDescription>
                  {showCodeEntry ? `We sent a 6-digit code to ${email_sent_to}` : "Sign in or create your account"}
                </CardDescription>
              </motion.div>
            </AnimatePresence>
          </CardHeader>

          <CardContent className="flex flex-col gap-10">
            <AnimatePresence mode="wait">
              {showCodeEntry ? (
                <motion.div
                  key="code-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <form onSubmit={handleCodeSubmit}>
                    <FieldGroup>
                      <Field>
                        <FieldLabel htmlFor="code">Verification code</FieldLabel>
                        <InputOTP
                          id="code"
                          name="code"
                          maxLength={6}
                          autoFocus
                          autoComplete="one-time-code"
                          value={codeForm.data.code}
                          onChange={(value) => codeForm.setData("code", value.toUpperCase())}
                        >
                          <InputOTPGroup className="gap-2.5 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border">
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                        <AnimatePresence>
                          {flash?.form_alert && (
                            <motion.p
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              transition={{ duration: 0.2 }}
                              className="text-red-600 text-sm"
                              dangerouslySetInnerHTML={{ __html: flash.form_alert }}
                            />
                          )}
                        </AnimatePresence>
                      </Field>

                      <Field className="gap-3">
                        <Button type="submit" disabled={codeForm.processing}>
                          <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                              key={codeForm.processing ? "verifying" : "verify"}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.15 }}
                            >
                              {codeForm.processing ? "Verifying..." : "Verify"}
                            </motion.span>
                          </AnimatePresence>
                        </Button>
                        <FieldDescription className="flex flex-col gap-1 text-center">
                          <span>
                            Didn&apos;t receive the code?{" "}
                            <a
                              href="#"
                              onClick={handleResend}
                              className="hover:text-black-brand underline decoration-transparent hover:decoration-slate-300 underline-offset-2"
                            >
                              Resend
                            </a>
                          </span>
                          <a
                            href="#"
                            onClick={handleUseDifferentEmail}
                            className="hover:text-black-brand underline decoration-transparent hover:decoration-slate-300 underline-offset-2"
                          >
                            Use a different email
                          </a>
                        </FieldDescription>
                      </Field>
                    </FieldGroup>
                  </form>
                </motion.div>
            ) : (
              <motion.div
                key="email-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-10"
              >
                <form action="/auth/google_oauth2" method="post" data-turbo="false">
                  <input type="hidden" name="authenticity_token" value={csrf_token} />
                  <Button variant="outline" type="submit" className="w-full">
                    <GoogleIcon className="w-5 h-5" />
                    Continue with Google
                  </Button>
                </form>

                <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                  Or continue with
                </FieldSeparator>

                <form onSubmit={handleEmailSubmit}>
                  <FieldGroup className="gap-4">
                    <Field>
                      <FieldLabel htmlFor="email">Email</FieldLabel>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        autoComplete="email"
                        value={emailForm.data.email}
                        onChange={(e) => emailForm.setData("email", e.target.value)}
                      />
                      <AnimatePresence>
                        {flash?.form_alert && (
                          <motion.p
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className="text-red-600 text-sm"
                            dangerouslySetInnerHTML={{ __html: flash.form_alert }}
                          />
                        )}
                      </AnimatePresence>
                    </Field>
                    <Field>
                      <Button type="submit" disabled={emailForm.processing}>
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.span
                            key={emailForm.processing ? "sending" : "continue"}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                          >
                            {emailForm.processing ? "Sending..." : "Continue with email"}
                          </motion.span>
                        </AnimatePresence>
                      </Button>
                    </Field>
                  </FieldGroup>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
          </CardContent>
        </Card>

        <FieldDescription className="px-6 text-sm text-center text-balance">
          By clicking continue, you agree to our{" "}
          <a href="https://decodekit.ai/legal/#terms" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
          &amp;{" "}
          <a href="https://decodekit.ai/legal/#privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </FieldDescription>
      </div>
    </AuthLayout>
  )
}
